import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import CustomTable from "src/components/Table/Table";

import {
  getBookingsListAction,
  assignDriverAction,
} from "src/api/bookings";

export default function Bookings() {
  const dispatch = useDispatch();

  const { bookingsList } = useSelector(
    (state: any) => state.bookings || {}
  );

  // ✅ FILTER STATES
  const [selectedSchool, setSelectedSchool] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const fetchData = async () => {
    await dispatch(getBookingsListAction({ pageNo: 1, pageSize: 10 }));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCaptainChange = async (row: any, captainId: string) => {
    await dispatch(
      assignDriverAction({
        bookingId: row.id,
        captainId,
      })
    );
    fetchData();
  };

  const openMap = (lat: string, lng: string) => {
    if (!lat || !lng) return;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  // ✅ UNIQUE SCHOOL LIST (FROM DATA)
  const schoolOptions = useMemo(() => {
    const set = new Set<string>();
    (bookingsList?.data || []).forEach((b: any) => {
      if (b.schoolName) set.add(b.schoolName);
    });
    return Array.from(set);
  }, [bookingsList]);

  // ✅ FILTERED DATA
  const filteredRows = useMemo(() => {
    return (bookingsList?.data || []).filter((row: any) => {
      const schoolMatch = selectedSchool
        ? row.schoolName === selectedSchool
        : true;

      const statusMatch = selectedStatus
        ? row.status === selectedStatus
        : true;

      return schoolMatch && statusMatch;
    });
  }, [bookingsList, selectedSchool, selectedStatus]);

  const columns = [
    { field: "parentName", headerName: "Parent", minWidth: 200 },
    { field: "parentPhone", headerName: "Phone", minWidth: 180 },
    { field: "childName", headerName: "Child", minWidth: 160 },
    { field: "grade", headerName: "Grade", minWidth: 100 },
    { field: "emergencyName", headerName: "Emergency Name", minWidth: 180 },
    { field: "emergencyPhone", headerName: "Emergency Phone", minWidth: 220 },
    { field: "address", headerName: "Address", minWidth: 220 },
    { field: "school", headerName: "School", minWidth: 220 },
    { field: "tenure", headerName: "Tenure", minWidth: 120 },
    { field: "startDate", headerName: "Start Date", minWidth: 180 },
    { field: "captain", headerName: "Captain", minWidth: 240 },
    { field: "status", headerName: "Status", minWidth: 180 },
  ];

  const actionContent = (column: string, row: any) => {
    if (column === "address") {
      return (
        <Tooltip title={row.address}>
          <Typography
            noWrap
            sx={{
              maxWidth: 200,
              color: "#1976d2",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => openMap(row.addressLat, row.addressLng)}
          >
            {row.address || "-"}
          </Typography>
        </Tooltip>
      );
    }

    if (column === "school") {
      return (
        <Tooltip title={row.schoolName}>
          <Typography
            noWrap
            sx={{
              maxWidth: 200,
              color: "#1976d2",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => openMap(row.schoolLat, row.schoolLng)}
          >
            {row.schoolName || "-"}
          </Typography>
        </Tooltip>
      );
    }

    if (column === "captain") {
      if (row.status === "Completed") {
        return (
          <Typography
            sx={{
              maxWidth: 220,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: 500,
            }}
          >
            {row.captainName
              ? `${row.captainName} (${row.phone})`
              : "-"}
          </Typography>
        );
      }

      return (
        <FormControl size="small" sx={{ width: 220 }}>
          <Select
            value={row.driverId || ""}
            onChange={(e) =>
              handleCaptainChange(row, e.target.value as string)
            }
            displayEmpty
            sx={{
              height: 36,
              "& .MuiSelect-select": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          >
            <MenuItem value="">
              <span style={{ color: "#9ca3af" }}>
                Assign Captain
              </span>
            </MenuItem>

            {(row.eligibleCaptains || []).map((c: any) => (
              <MenuItem key={c.id} value={c.id}>
                {c.fullName} ({c.phone})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (column === "status") {
      return (
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: 500,
            color:
              row.status === "Assigned"
                ? "#117C39"
                : row.status === "Completed"
                ? "#2563eb"
                : "#BA2222",
          }}
        >
          {row.status}
        </Typography>
      );
    }

    return null;
  };

  return (
    <Box sx={{ mt: "30px", p: 3 }}>
      {/* ✅ FILTERS */}
      <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mb={2}
    >
      {/* LEFT: TITLE */}
      <Typography fontSize={22} fontWeight={600}>
        
      </Typography>

      {/* RIGHT: FILTERS */}
      <Box display="flex" gap={2}>
        {/* SCHOOL FILTER */}
        <FormControl size="small">
          <Select
            value={selectedSchool}
            onChange={(e) => setSelectedSchool(e.target.value)}
            displayEmpty
            sx={{
              minWidth: 220,
              height: 40,
              borderRadius: "12px",
              backgroundColor: "#f8fafc",
              "& fieldset": {
                borderColor: "#e2e8f0",
              },
              "&:hover fieldset": {
                borderColor: "#cbd5e1",
              },
            }}
          >
            <MenuItem value="">All Schools</MenuItem>
            {schoolOptions.map((school) => (
              <MenuItem key={school} value={school}>
                {school}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* STATUS FILTER */}
        <FormControl size="small">
          <Select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            displayEmpty
            sx={{
              minWidth: 180,
              height: 40,
              borderRadius: "12px",
              backgroundColor: "#f8fafc",
              "& fieldset": {
                borderColor: "#e2e8f0",
              },
              "&:hover fieldset": {
                borderColor: "#cbd5e1",
              },
            }}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Assigned">Assigned</MenuItem>
            <MenuItem value="Completed">Completed</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>

      {/* TABLE */}
      <Box
        sx={{
          "& .MuiTableContainer-root": {
            overflowX: "auto",

            "&::-webkit-scrollbar": { height: "6px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f5f9",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#cbd5e1",
              borderRadius: "10px",
            },
          },

          "& .MuiTable-root": {
            minWidth: "1700px",
          },
        }}
      >
        <CustomTable
          rows={filteredRows}
          columns={columns}
          total={filteredRows.length}
          fetchData={() => {}}
          resetPagination={() => {}}
          action={actionContent}
        />
      </Box>
    </Box>
  );
}